import {
  IMAGE_TOO_LARGE_MESSAGE,
  pickOptimizeAndUploadImage,
  type ImageOptimizationStep,
  type ImageUploadProgress,
  type OptimizedImageUploadResult,
} from "@/lib/imageUpload";

export type BrandImageKind = "logo" | "profile";
export type ImageFitMode = "cover" | "contain";
export type ItemImageUploadStatus = string;

export type UploadedImageResult = OptimizedImageUploadResult & {
  url: string;
  fit: ImageFitMode;
};

type UploadCallbacks = {
  onStatusChange?: (status: ItemImageUploadStatus) => void;
  onProgress?: (progress: ImageUploadProgress) => void;
};

export const ITEM_IMAGE_TOO_LARGE_MESSAGE = IMAGE_TOO_LARGE_MESSAGE;

const BRAND_LOGO_OPTIMIZATION_STEPS: ImageOptimizationStep[] = [
  { longestSide: 1200, quality: 0.84 },
  { longestSide: 1000, quality: 0.76 },
  { longestSide: 900, quality: 0.68 },
  { longestSide: 800, quality: 0.6 },
  { longestSide: 700, quality: 0.52 },
];

const BRAND_PROFILE_OPTIMIZATION_STEPS: ImageOptimizationStep[] = [
  { longestSide: 1200, quality: 0.84 },
  { longestSide: 1000, quality: 0.76 },
  { longestSide: 900, quality: 0.68 },
  { longestSide: 800, quality: 0.6 },
  { longestSide: 700, quality: 0.52 },
];

const ITEM_IMAGE_OPTIMIZATION_STEPS: ImageOptimizationStep[] = [
  { longestSide: 1600, quality: 0.82 },
  { longestSide: 1400, quality: 0.75 },
  { longestSide: 1200, quality: 0.68 },
  { longestSide: 1000, quality: 0.6 },
  { longestSide: 900, quality: 0.55 },
  { longestSide: 800, quality: 0.5 },
  { longestSide: 700, quality: 0.45 },
];

function notifyCallbacks(
  callbacks: UploadCallbacks | undefined,
  progress: ImageUploadProgress
) {
  callbacks?.onProgress?.(progress);
  callbacks?.onStatusChange?.(progress.message);
}

function withUrlAlias(
  uploaded: OptimizedImageUploadResult,
  fit: ImageFitMode
): UploadedImageResult {
  return {
    ...uploaded,
    url: uploaded.downloadURL,
    fit,
  };
}

export async function pickAndUploadBrandImage(params: {
  businessId: string;
  kind: BrandImageKind;
  onStatusChange?: (status: ItemImageUploadStatus) => void;
  onProgress?: (progress: ImageUploadProgress) => void;
}): Promise<UploadedImageResult> {
  const { businessId, kind, onStatusChange, onProgress } = params;
  const fit = kind === "logo" ? "contain" : "cover";
  const storagePath = `businesses/${businessId}/brand/${kind}-${Date.now()}.jpg`;

  const uploaded = await pickOptimizeAndUploadImage({
    storagePath,
    optimizationSteps:
      kind === "profile"
        ? BRAND_PROFILE_OPTIMIZATION_STEPS
        : BRAND_LOGO_OPTIMIZATION_STEPS,
    onProgress: (progress) =>
      notifyCallbacks({ onStatusChange, onProgress }, progress),
  });

  return withUrlAlias(uploaded, fit);
}

export async function pickAndUploadItemImage(params: {
  businessId: string;
  itemId: string;
  onStatusChange?: (status: ItemImageUploadStatus) => void;
  onProgress?: (progress: ImageUploadProgress) => void;
}): Promise<UploadedImageResult> {
  const { businessId, itemId, onStatusChange, onProgress } = params;
  const storagePath = `businesses/${businessId}/items/${itemId}-${Date.now()}.jpg`;
  const uploaded = await pickOptimizeAndUploadImage({
    storagePath,
    optimizationSteps: ITEM_IMAGE_OPTIMIZATION_STEPS,
    onProgress: (progress) =>
      notifyCallbacks({ onStatusChange, onProgress }, progress),
  });

  return withUrlAlias(uploaded, "cover");
}
